import React, { useState, useEffect } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import _ from "lodash";
import qs from "qs";

import { verifyPasswordResetCode, confirmPasswordReset, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../firebase.js";
import AuthLayout from "../../components/AuthLayout.js";
import Message from "../../components/Message.js";
import Button from "../../components/Button.js";

const errorMessages = {
  default: "Het is niet gelukt om je password te resetten, probeer het later opnieuw.",
  "auth/argument-error": "De reset link die u gebruikt is ongeldig. Vraag een nieuwe aan.",
  "auth/invalid-action-code": "Helaas! Net te laat, deze reset link is verlopen. Vraag een nieuwe aan.",
  "auth/expired-action-code": "Helaas! Deze reset link is verlopen. Vraag een nieuwe aan.",
  "auth/weak-password": "Je wachtwoord is te zwak. Gebruik minimaal 6 tekens.",
  "auth/user-disabled": "Dit account is gedeactiveerd. Neem contact op met support.",
  "auth/user-not-found": "Dit account bestaat niet meer. Neem contact op met support.",
};

function ResetPassword() {
  const [password, setPassword] = useState("");
  const [passwordCheck, setPasswordCheck] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const actionCode = query.oobCode;

  useEffect(() => {
    async function validateCode() {
      try {
        const email = await verifyPasswordResetCode(auth, actionCode);
        setVerifiedEmail(email);
      } catch (err) {
        console.error(err);
        setError(errorMessages[err.code] || errorMessages["default"]);
      }
    }
    validateCode();
  }, [actionCode]);

  if (auth.currentUser && !auth.currentUser.isAnonymous) {
    return <Navigate replace to={_.get(location.state, "from", "/")} />;
  }

  async function onSubmit(event) {
    event.preventDefault();
    setError("");

    if (password !== passwordCheck) {
      setError("De velden komen niet overeen.");
      return;
    }

    try {
      await confirmPasswordReset(auth, actionCode, password);
      setInfo("Je wachtwoord is gewijzigd, je wordt doorgestuurd...");
      await signInWithEmailAndPassword(auth, verifiedEmail, password);
      navigate("/", { replace: true });
    } catch (err) {
      console.error(err);
      setError(errorMessages[err.code] || errorMessages["default"]);
    }
  }

  return (
    <AuthLayout>
      <form id="reset-password" onSubmit={onSubmit}>
        <h2 className="text-2xl font-heading font-bold mb-2">Nieuw wachtwoord</h2>
        <p className="text-sm text-text-secondary mb-6">Kies je nieuwe wachtwoord.</p>

        {!!info && (
          <div className="mb-4">
            <Message variant="info" text={info} />
          </div>
        )}
        {!!error && (
          <div className="mb-4">
            <Message text={error} />
          </div>
        )}

        <div className="space-y-4 mb-6">
          <div>
            <label className="label-base">Nieuw wachtwoord</label>
            <input
              name="password"
              type="password"
              autoComplete="new-password"
              minLength="8"
              placeholder="Nieuw wachtwoord"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-base"
            />
          </div>
          <div>
            <label className="label-base">Bevestig wachtwoord</label>
            <input
              name="password-check"
              type="password"
              autoComplete="new-password"
              minLength="8"
              placeholder="Nieuw wachtwoord bevestigen"
              value={passwordCheck}
              onChange={(e) => setPasswordCheck(e.target.value)}
              className="input-base"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button type="submit" label="Wachtwoord wijzigen" className="flex-1" />
          <Link
            to="/login"
            className="inline-flex items-center justify-center min-h-button px-4 py-2 text-base font-medium rounded-sm text-accent hover:underline underline-offset-2"
          >
            Annuleren
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export default ResetPassword;
